export const DASHBOARDS_LIST_ACTIONS = {
  CREATE: 'create',
  EDIT: 'edit',
  DELETE: 'delete',
  SHARE: 'share',
  FILTERS: 'filters',
} as const;

export type DashboardUserAffordance =
  typeof DASHBOARDS_LIST_ACTIONS[keyof typeof DASHBOARDS_LIST_ACTIONS];

export const DASHBOARD_DETAIL_ACTIONS = {
  EDIT: 'edit',
  DELETE: 'delete',
  EXPORT_PDF: 'export-pdf',
  EXPORT_DATA: 'export-data',
  VIEW_INSIGHT_DATA: 'view-insight-data',
} as const;

export const DASHBOARDS_TABLE_SORTABLE_COLUMNS = {
  NAME: 'name',
  OWNER: 'ownerName',
  CREATED_ON: 'createdAt',
  LAST_UPDATED: 'updatedAt',
  PERMISSIONS: 'permissions',
} as const;

export const INSIGHT_IDS = {
  REQUESTS_RECEIVED: 'requests_received',
  RESPONSE_RATIO: 'response_ratio',
  REQUESTS_APPROVED: 'requests_approved',
  DECLINED_REQUESTS_BY_REASON: 'declined_requests_by_reason',
  REQUESTS_IN_REVIEW: 'requests_in_review',
  REQUESTS_BY_STATUS: 'requests_by_status',
  REQUEST_RESOLUTION_REQUEST_TYPE_BT: 'request_resolution_request_type_bt',
  PROGRAMS_DELIVERY_TYPE: 'programs_by_delivery_type',
  PROGRAMS_STATUS_BREAKDOWN_DELIVERY_TYPE:
    'programs_status_breakdown_delivery_type',
  REQUEST_BREAKDOWN_BY_BUSINESS_TEAMS: 'request_breakdown_by_business_teams',
  REQUEST_BREAKDOWN_BY_PRIORITY: 'request_breakdown_by_priority',
  REQUEST_BREAKDOWN_BY_TYPE: 'request_breakdown_by_type',
  PROJECTS_CREATED: 'projects_created',
  PROJECTS_STARTED: 'projects_started',
  PROJECTS_TARGETED: 'projects_targeted',
  PROJECTS_COMPLETED: 'projects_completed',
  PROJECTS_LAUNCHED: 'projects_launched',
  TOTAL_DEMAND_HOURS: 'total_demand_hours',
  PROJECTS_BY_DELIVERY_STATUS: 'projects_by_delivery_status',
  PROJECTS_STATUS_BY_BUSINESS_UNIT: 'project_status_by_business_unit',
  PROJECTS_STATUS_BY_PROGRAM: 'project_status_by_program',
  PROJECTS_STATUS_BY_CATEGORY: 'project_status_by_category',
  PROJECTS_DEMAND_HOURS_BY_BUSINESS_UNIT:
    'projects_demand_hours_by_business_unit',
  PROJECTS_DEMAND_HOURS_BY_PROGRAM: 'projects_demand_hours_by_program',
  PROJECTS_DEMAND_HOURS_BY_CATEGORY: 'projects_demand_hours_by_category',
  PROJECTS_ACROSS_STAGES: 'projects_across_stages',
  ALLOCATION_RATE_BY: 'allocation_rate_by',
  ESTIMATED_VERSUS_ACTUAL_HOURS_TASK_TYPE:
    'estimated_versus_actual_hours_task_types',
  ESTIMATED_VERSUS_ACTUAL_COMPLETION_DAYS_PROJECT_CATEGORY:
    'estimated_versus_actual_completion_days_project_category',
  PROJECT_LINKED_REQUESTS_BY_STATUS: 'project_linked_requests_status',
  PROJECTS_BY_STATUS: 'projects_by_status',
  PROJECTS_AND_DEMAND_HOURS: 'projects_and_demand_hours',
  TASKS_BY_STATUS: 'tasks_by_status',
  USERS_BY_TYPE: 'users_by_type',
  RESOURCE_WORKLOAD_OVERVIEW: 'resource_workload_overview',
  ALLOCATED_ACTUAL_HOURS_BY_USER_TYPE: 'allocated_actual_hours_by_user_type',
  PROJECTED_VS_ACTUAL_UTILIZATION: 'projected_vs_actual_utilization',
  PROJECTED_VS_ACTUAL_UTILIZATION_BY_TEAMS:
    'projected_vs_actual_utilization_by_teams',
  PROJECTED_VS_ACTUAL_UTILIZATION_BY_JOB_FUNCTION:
    'projected_vs_actual_utilization_by_job_function',
  PROJECTED_VS_ACTUAL_UTILIZATION_BY_USERS:
    'projected_vs_actual_utilization_by_users',
  CAPACITY_HOURS: 'capacity_hours',
  AVAILABLE_HOURS: 'available_hours',
  ALLOCATED_HOURS: 'allocated_hours',
  ACTUAL_HOURS: 'actual_hours',
} as const;

export const REQUEST_DRILLDOWN_TABLE_SORTABLE_COLUMNS = {
  REQUEST_NUMBER: 'Request Number',
  REQUEST_TITLE: 'Request Title',
  STATUS: 'Status',
  TYPE: 'Type',
  REQUESTER_NAME: 'Requester Name',
  REQUEST_OWNER: 'Request Owner',
  PROJECTS: 'Projects',
  BUSINESS_UNIT: 'Business Unit',
  INTERNAL_PRIORITY: 'Internal Priority',
  FORM_USED: 'Form Used',
  CREATION_DATE: 'Creation Date',
  SUBMISSION_DATE: 'Submission Date',
  DECISION_DATE: 'Decision Date',
  LAST_UPDATE: 'Last Update',
} as const;

export const EVENT_DRILLDOWN_TABLE_SORTABLE_COLUMNS = {
  EVENT_TITLE: 'title',
  EVENT_TYPE: 'event_type',
  START: 'start_timestamp',
  END: 'end_timestamp',
  FACILITATORS: 'facilitator_count',
  RECURRING_EVENT: 'is_recurring',
  NUMBER_OF_DAYS: 'event_days',
  DAILY_DURATION: 'duration_hours_per_day',
  TOTAL_DURATION: 'total_duration_hours',
  NUMBER_OF_DAYS_IN_RANGE: 'event_days_in_range',
  TOTAL_DURATION_IN_RANGE: 'duration_hours_in_range',
} as const;

export const PROJECT_DRILLDOWN_TABLE_SORTABLE_COLUMNS = {
  PROJECT_NUMBER: 'Project Number',
  TITLE: 'Project Name',
  PROJECT_STATUS: 'Status',
  PROJECT_PRIORITY: 'Priority',
  PROJECT_HEALTH: 'Health',
  RESOURCING_TYPE: 'Resourcing Type',
  CATEGORY_NAME: 'Category Name',
  PROCESS_NAME: 'Process Name',
  STAGE_NAME: 'Stage Name',
  TOTAL_DEMAND_HOURS: 'Total Demand Hours',
  PROJECT_OWNER: 'Project Owner',
  BUSINEES_UNITS: 'Business Units',
  PROGRAMS: 'Programs',
  START_DATE: 'Start Date',
  TARGET_COMPLETION_DATE: 'Target Completion Date',
  COMPLETION_DATE: 'Actual Completion Date',
} as const;

export const TASK_DRILLDOWN_TABLE_SORTABLE_COLUMNS = {
  TASK_NUMBER: 'Task Number',
  TASK_NAME: 'Task Name',
  STATUS: 'Status',
  TYPE: 'Type',
  PROJECT: 'Project',
  PROGRAMS: 'Programs',
  ASSIGNEES: 'Assignees',
  START_DATE: 'Start Date',
  DUE_DATE: 'Due Date',
  DEMAND_EST_HOURS: 'Demand (EST) Hours',
  ALLOCATED_HOURS: 'Allocated Hours',
  ACTUAL_HOURS: 'Actual Hours',
} as const;

export const USER_DRILLDOWN_TABLE_SORTABLE_COLUMNS = {
  USER_ID: 'User Id',
  NAME: 'Name',
  TIME_OFF: 'Time Off',
  DATE: 'Date',
  TYPE: 'Type',
  ROLE: 'Role',
  EMAIL: 'Email',
  LAST_LOGIN_DATE: 'Last Login Date',
  DISABLED_AT: 'Disabled At',
  CREATED_AT: 'Created At',
  JOB_FUNCTIONS: 'Job Functions',
  SKILLS: 'Skills',
  LEARNING_TEAMS: 'Learning Teams',
  FIRST_NAME: 'First Name',
  LAST_NAME: 'Last Name',
  DEFAULT_CAPACITY_HOURS: 'Default Capacity Hours',
  SCHEDULED_HOURS: 'Scheduled Hours',
  CAPACITY_HOURS: 'Capacity hours',
  AVAILABLE_HOURS: 'Available Hours',
  ALLOCATED_HOURS: 'Allocated Hours',
  ACTUAL_HOURS: 'Actual Hours',
  TASK_NUMBER: 'Task Number',
  TASK_TITLE: 'Task Title',
  PROJECT_NUMBER: 'Project Number',
  PROJECT_TITLE: 'Project Title',
  JOB_TITLE: 'Job Title',
  USER_TYPE: 'User Type',
  TEAMS: 'Teams',
} as const;

export const CAPACITY_MANAGEMENT_DRILLDOWN_TABLE_SORTABLE_COLUMNS = {
  NAME: 'Name',
  USER_TYPE: 'User Type',
  TASK_NUMBER: 'Task Number',
  TASK_TITLE: 'Task Title',
  TASK_STATUS: 'Task Status',
  TASK_TYPE: 'Task Type',
  ALLOCATED_HOURS: 'Allocated Hours',
  ACTUAL_HOURS: 'Actual Hours',
  PROJECT_TITLE: 'Project Title',
  BUSINESS_UNITS: 'Business Units',
  PROGRAMS: 'Programs',
  PROJECT_TEAMS: 'Project Teams',
  USER_TEAMS: 'User Teams',
} as const;

export const CHART_COLORS = [
  '#55AC9B',
  '#C06892',
  '#5DA1DC',
  '#B5C7F5',
  '#438A7E',
  '#9B3A66',
  '#7F94C8',
  '#E97C59',
  '#3389D3',
  '#327569',
  '#8B2C56',
  '#5A73B0',
  '#E46136',
  '#216199',
  '#2F5E55',
  '#64203E',
  '#45598C',
  '#B8350A',
  '#1B3F39',
];

export const INSIGHT_CHART_FONT_CHAR_WIDTH = 9;

export const DRILLDOWN_FILTER_KEY_MAP: Record<string, string> = {
  business_unit_id: 'filter[business-unit-id]',
  request_type: 'filter[request-type]',
  request_status: 'filter[request-status]',
  ld_priority: 'filter[request-priority]',
  declination_reason: 'filter[request-declination-reason]',
  program_id: 'filter[program-id]',
  category_id: 'filter[project-category-id]',
  project_status: 'filter[project-status]',
  task_status: 'filter[task-status]',
  user_type: 'filter[user-type]',
  job_function: 'filter[job-function]',
  team_id: 'filter[learning-team-id]',
  user_id: 'filter[user-id]',
} as const;
